import React, { useEffect, useState } from 'react';
import { Button, Form, InputNumber } from 'antd';
import {
  fetchPriceRounding,
  fetchPriceRoundingConfig,
  updatePriceRoundingConfig,
} from './api';
import { PriceRoundingConfigData, PriceRoundingData } from './types';
import message from '~/utils/message';
import LoadingSpinner from '../components/icons/LoadingSpinner';

export const inputFormatter = (val?: number | null) => {
  if (val == null) return '';
  return `${val}`
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    .replace(/\.(?=\d{0,2}$)/g, ',');
};

export const inputParser = (val?: string | null) => {
  if (!val) return 0;
  return Number.parseFloat(
    val.replace(/\$\s?|(\.*)/g, '').replace(/(,{1})/g, '.')
  );
};

const PriceRoundingForm = ({
  initialValues,
}: {
  initialValues?: { roundingUnit: number; roundingThreshold: number };
}) => {
  const [loading, setLoading] = useState(false);
  const onSubmit = async (data: PriceRoundingConfigData) => {
    try {
      setLoading(true);
      await updatePriceRoundingConfig(data);
      message.success('Succesfully saved!');
    } catch (e) {
      message.error(`Fail to save: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      name="login"
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSubmit}
      autoComplete="off"
    >
      <Form.Item
        label="Kelipatan"
        name="roundingUnit"
        rules={[{ required: true, message: 'Wajib diisi' }]}
      >
        <InputNumber
          className="w-full"
          formatter={inputFormatter}
          parser={inputParser}
          min={0}
        />
      </Form.Item>

      <Form.Item
        label="Minimal"
        name="roundingThreshold"
        rules={[{ required: true, message: 'Wajib diisi' }]}
      >
        <InputNumber
          className="w-full"
          formatter={inputFormatter}
          parser={inputParser}
          min={0}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

const TestRounding = () => {
  const [value, setValue] = useState<number | null>(null);
  const [result, setResult] = useState<PriceRoundingData | null>(null);

  const onClick = async () => {
    if (!value) return;
    try {
      const { data } = await fetchPriceRounding(value);
      setResult(data);
    } catch (_) {
      message.error('Server error!');
    }
  };

  return (
    <div className="mt-[40px]">
      <p className="text-xl mb-3">Test</p>
      <div className="flex gap-1 items-center">
        <InputNumber
          value={value}
          onChange={setValue}
          className="flex-1"
          formatter={inputFormatter}
          parser={inputParser}
          placeholder="Total price"
        />
        <Button type="primary" onClick={onClick}>
          Round
        </Button>
      </div>
      {result ? (
        <div className="w-[200px] mt-3">
          <div className="flex justify-between">
            <p>Rounding</p>
            <p>{`-${inputFormatter(result.roundingAmount) ?? 0}`}</p>
          </div>
          <div className="flex justify-between mt-1">
            <p>Final</p>
            <p>{inputFormatter(result.roundedTotalPrice)}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PriceRounding = () => {
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<PriceRoundingConfigData>();

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await fetchPriceRoundingConfig();
        if (!current) return;
        setInitialValues(data);
        setLoading(false);
      } catch (_) {
        message.error('Server error!');
      }
    })();
    return () => {
      current = false;
    };
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-[500px] p-6">
      <h1 className="text-2xl mb-6">Price Rounding</h1>
      <PriceRoundingForm initialValues={initialValues} />
      <TestRounding />
    </div>
  );
};

export default PriceRounding;
